'use client';

import { useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import type { WatchlistGroup, MarketCoin, SearchResult } from '@glintfans/shared';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPrice, formatPercent, formatVolume } from '@/lib/format';
import { useRealtimeTickers, mergeLive } from '@/lib/realtime';
import CoinLogo from '@/components/CoinLogo';
import PullToRefresh from '@/components/PullToRefresh';
import { ListSkeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';

interface GroupWithItems extends WatchlistGroup {
  items: Array<{
    id: string;
    groupId: string;
    symbol: string;
    exchange?: string;
    product?: string;
    sortOrder: number;
    coin?: MarketCoin | null;
  }>;
}

type SortMode = 'manual' | 'price' | 'change' | 'volume';

const SORT_MODES: SortMode[] = ['manual', 'price', 'change', 'volume'];

/** D14: 市场身份键 —— 多交易所自选不能靠 symbol 去重 */
const marketKey = (symbol: string, exchange?: string, product?: string) =>
  `${symbol}::${exchange || ''}::${product || ''}`;

export default function WatchlistPage() {
  const { language } = useAppStore();
  const t = translate(language);
  const qc = useQueryClient();
  const live = useRealtimeTickers();
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('manual');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [showAddCoin, setShowAddCoin] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [groupMenuFor, setGroupMenuFor] = useState<string | null>(null);
  // D14: 「仅看异常」筛选（|24h 涨跌| ≥ 5% 视为异常）+ 行内更多菜单
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [itemMenuFor, setItemMenuFor] = useState<string | null>(null);
  const ANOMALY_THRESHOLD_PCT = 5;

  const { data: groups, isLoading } = useQuery({
    queryKey: ['watchlist'],
    queryFn: () => api.get<GroupWithItems[]>('/watchlist'),
  });

  const createGroup = useMutation({
    mutationFn: (name: string) => api.post('/watchlist/groups', { name }),
    onSuccess: () => {
      setNewGroupName('');
      setShowNewGroup(false);
      qc.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });

  const addItem = useMutation({
    mutationFn: ({ groupId, symbol, exchange, product }: { groupId: string; symbol: string; exchange?: string; product?: string }) =>
      api.post(`/watchlist/groups/${groupId}/items`, { symbol, exchange, product }),
    onSuccess: (_d, v) => {
      trackEvent('watchlist_add', v.symbol);
      qc.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });

  // D14: 删除后提供「撤销」恢复（5 秒内），减少误触损失
  const [lastRemoved, setLastRemoved] = useState<{ itemId: string; groupId: string; symbol: string; exchange?: string; product?: string } | null>(null);
  const [undoTimer, setUndoTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const lastRemovedRef = useRef<{ itemId: string; symbol: string } | null>(null);

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.delete(`/watchlist/items/${itemId}`),
    onSuccess: (_d, itemId) => {
      const removed = lastRemovedRef.current;
      if (removed && removed.itemId === itemId) trackEvent('watchlist_remove', removed.symbol);
      qc.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });

  const doRemove = (item: { id: string; groupId: string; symbol: string; exchange?: string; product?: string }) => {
    removeItem.mutate(item.id);
    lastRemovedRef.current = { itemId: item.id, symbol: item.symbol };
    setLastRemoved({ itemId: item.id, groupId: item.groupId, symbol: item.symbol, exchange: item.exchange, product: item.product });
    if (undoTimer) clearTimeout(undoTimer);
    setUndoTimer(
      setTimeout(() => setLastRemoved(null), 5000),
    );
  };

  const undoRemove = () => {
    if (!lastRemoved) return;
    addItem.mutate({
      groupId: lastRemoved.groupId,
      symbol: lastRemoved.symbol,
      exchange: lastRemoved.exchange,
      product: lastRemoved.product,
    });
    if (undoTimer) clearTimeout(undoTimer);
    setLastRemoved(null);
  };

  const deleteGroup = useMutation({
    mutationFn: (groupId: string) => api.delete(`/watchlist/groups/${groupId}`),
    onSuccess: () => {
      setGroupMenuFor(null);
      qc.invalidateQueries({ queryKey: ['watchlist'] });
    },
  });

  const { data: searchRes } = useQuery({
    queryKey: ['search', searchQ],
    queryFn: () => api.get<SearchResult>(`/market/search?q=${encodeURIComponent(searchQ)}`),
    enabled: searchQ.trim().length > 0,
  });

  const activeGroupId2 = activeGroupId ?? groups?.[0]?.id ?? null;
  const activeGroup = (groups ?? []).find((g) => g.id === activeGroupId2) ?? null;

  const sortedItems = useMemo(() => {
    if (!activeGroup) return [];
    const items = activeGroup.items.slice();
    // UI04: in-group search
    const q = groupSearch.trim().toLowerCase();
    if (q) {
      const filtered = items.filter((i) => i.symbol.toLowerCase().includes(q));
      return filtered;
    }
    if (sortMode === 'manual') return items;
    const getCoin = (it: { coin?: MarketCoin | null }) =>
      it.coin ? mergeLive(it.coin, live[it.coin.symbol]) : null;
    if (sortMode === 'price') {
      items.sort((a, b) => ((getCoin(b)?.price ?? 0) - (getCoin(a)?.price ?? 0)));
    } else if (sortMode === 'change') {
      items.sort((a, b) => ((getCoin(b)?.priceChangePercent24h ?? 0) - (getCoin(a)?.priceChangePercent24h ?? 0)));
    } else if (sortMode === 'volume') {
      items.sort((a, b) => ((getCoin(b)?.quoteVolume24h ?? 0) - (getCoin(a)?.quoteVolume24h ?? 0)));
    }
    return items;
  }, [activeGroup, sortMode, live, groupSearch]);

  // D14: 「仅看异常」—— |24h 涨跌| ≥ 阈值才显示
  const displayItems = useMemo(() => {
    if (!anomalyOnly) return sortedItems;
    return sortedItems.filter((it) => {
      const coin = it.coin ? mergeLive(it.coin, live[it.coin.symbol]) : null;
      return Math.abs(coin?.priceChangePercent24h ?? 0) >= ANOMALY_THRESHOLD_PCT;
    });
  }, [sortedItems, anomalyOnly, live]);

  // D14: 多交易所自选不能靠 symbol 去重 —— 用 symbol+exchange+product 判断已添加
  const inGroup = (symbol: string, exchange?: string, product?: string) =>
    activeGroup?.items.some(
      (i) => marketKey(i.symbol, i.exchange, i.product) === marketKey(symbol, exchange, product),
    ) ?? false;

  if (isLoading) return <ListSkeleton rows={5} />;

  const searchPairs = [
    ...(searchRes?.pairs ?? []).map((p) => ({
      symbol: p.symbol,
      baseAsset: p.baseAsset,
      quoteAsset: p.quoteAsset,
      exchange: (p as any).exchange || 'Binance',
      isPerp: !p.isSpotTradingAllowed || p.contractType === 'PERPETUAL',
      product: !p.isSpotTradingAllowed || p.contractType === 'PERPETUAL' ? 'futures' : 'spot',
    })),
    ...(searchRes?.coins ?? [])
      .filter((c) => !(searchRes?.pairs ?? []).some((p) => p.symbol === c.symbol))
      .map((c) => ({
        symbol: c.symbol,
        baseAsset: c.baseAsset,
        quoteAsset: c.quoteAsset,
        exchange: (c as any).exchange || 'Binance',
        isPerp: true,
        product: 'futures' as string,
      })),
  ]
    // D14: 搜索排序 —— 精确市场 → 基础资产 → 名称 → 报价币
    .sort((a, b) => {
      const q = searchQ.trim().toUpperCase();
      if (!q) return 0;
      const sa = a.symbol.toUpperCase();
      const sb = b.symbol.toUpperCase();
      const score = (s: string): number => {
        if (s === q) return 0;                 // 精确市场
        if (s.startsWith(q)) return 1;         // 前缀
        if (s.includes(q)) return 2;           // 包含
        return 3;
      };
      const da = Math.min(score(sa), score(a.baseAsset.toUpperCase()), score(`${a.baseAsset}${a.quoteAsset}`.toUpperCase()));
      const db = Math.min(score(sb), score(b.baseAsset.toUpperCase()), score(`${b.baseAsset}${b.quoteAsset}`.toUpperCase()));
      if (da !== db) return da - db;
      return a.symbol.localeCompare(b.symbol);
    })
    .slice(0, 30);

  return (
    <PullToRefresh
      onRefresh={async () => {
        qc.invalidateQueries({ queryKey: ['watchlist'] });
      }}
      refreshingLabel={language !== 'en' ? '自选刷新中…' : 'Refreshing…'}
      pullLabel={language !== 'en' ? '下拉刷新自选' : 'Pull to refresh'}
    >
    <div>
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{t.watchlist.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={groupSearch}
            onChange={(e) => setGroupSearch(e.target.value)}
            placeholder={language !== 'en' ? '搜寻组内币种' : 'Search in group'}
            className="input !w-40 !py-1.5 text-xs"
          />
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            className="input !w-auto !py-1.5 text-xs"
            aria-label={t.watchlist.sort}
          >
            {SORT_MODES.map((m) => (
              <option key={m} value={m}>
                {t.watchlist['sort' + (m === 'manual' ? 'Manual' : m === 'price' ? 'Price' : m === 'change' ? 'Change' : 'Volume')]}
              </option>
            ))}
          </select>
          <button className="btn-ghost !h-8 !px-3 text-xs" onClick={() => setShowNewGroup(true)}>
            + {t.watchlist.newGroup}
          </button>
          <button className="btn-primary !h-8 !px-3 text-xs" onClick={() => setShowAddCoin(true)}>
            + {t.watchlist.addCoin}
          </button>
        </div>
      </div>

      {/* D14: 删除后撤销条（5 秒内可恢复） */}
      {lastRemoved && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-xs">
          <span className="text-gf-muted">
            {t.markets.removed} {lastRemoved.symbol}
            {lastRemoved.exchange ? ` · ${lastRemoved.exchange}` : ''}
          </span>
          <button className="shrink-0 font-medium text-gf-accent hover:underline" onClick={undoRemove}>
            {t.markets.undo}
          </button>
        </div>
      )}

      {(groups ?? []).length === 0 ? (
        <div className="card">
          <EmptyState
            title={t.watchlist.empty}
            description={language !== 'en' ? '建立第一个自选组，开始追踪你的币种' : 'Create your first group to start tracking coins.'}
            actionLabel={t.watchlist.newGroup}
            onAction={() => setShowNewGroup(true)}
          />
        </div>
      ) : (
        <>
          {/* Group tabs */}
          <div className="mb-3 flex gap-1 overflow-x-auto whitespace-nowrap pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {(groups ?? []).map((g) => (
              <button
                key={g.id}
                onClick={() => setActiveGroupId(g.id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  g.id === activeGroupId2
                    ? 'border-gf-accent bg-gf-accent/10 text-gf-accent'
                    : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
                }`}
              >
                <span>{g.name}</span>
                <span className={`rounded px-1 text-[9px] ${g.id === activeGroupId2 ? 'bg-gf-accent/20' : 'bg-gf-surface2'}`}>
                  {g.items.length}
                </span>
              </button>
            ))}
          </div>

          {activeGroup ? (
            <div className="card !p-0 overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gf-border px-4 py-2.5">
                <span className="font-medium">{activeGroup.name}</span>
                <div className="relative flex items-center gap-2">
                  {/* D14: 每组「仅看异常」筛选 */}
                  <button
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                      anomalyOnly
                        ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                        : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
                    }`}
                    onClick={() => setAnomalyOnly((v) => !v)}
                  >
                    {t.markets.onlyAnomalies}
                  </button>
                  <span className="text-xs text-gf-muted">
                    {displayItems.length}/{activeGroup.items.length}
                  </span>
                  <button
                    className="rounded px-1.5 text-gf-muted hover:bg-gf-surface2"
                    onClick={() => setGroupMenuFor(groupMenuFor === activeGroup.id ? null : activeGroup.id)}
                  >
                    ⋮
                  </button>
                  {groupMenuFor === activeGroup.id && (
                    <div className="absolute right-0 top-7 z-20 rounded-lg border border-gf-border bg-gf-surface p-1 shadow-xl">
                      <button
                        className="block w-full whitespace-nowrap rounded px-3 py-1.5 text-left text-xs text-gf-negative hover:bg-gf-surface2"
                        onClick={() => {
                          if (window.confirm(t.watchlist.deleteGroupConfirm)) deleteGroup.mutate(activeGroup.id);
                        }}
                      >
                        {t.watchlist.deleteGroup}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {displayItems.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="mb-3 text-xs text-gf-muted">
                    {sortedItems.length > 0
                      ? (language !== 'en' ? '没有符合「仅看异常」的币种' : 'No coins match "only anomalies"')
                      : t.watchlist.groupEmpty}
                  </div>
                  {sortedItems.length === 0 && (
                    <button className="btn-primary !h-8 text-xs" onClick={() => setShowAddCoin(true)}>
                      + {t.watchlist.addCoin}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden overflow-x-auto sm:block">
                    <table className="table-base">
                      <thead className="bg-gf-surface2/60">
                        <tr>
                          <th className="text-left">{t.markets.colSymbol}</th>
                          <th className="text-right">{t.markets.colPrice}</th>
                          <th className="text-right">{t.markets.colChange}</th>
                          <th className="text-right">{t.markets.colVolume}</th>
                          <th className="text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayItems.map((item) => {
                          const coin = item.coin ? mergeLive(item.coin, live[item.coin.symbol]) : null;
                          const pct = coin?.priceChangePercent24h ?? 0;
                          const isSpot = (item.product === 'spot');
                          const base = coin?.baseAsset ?? item.symbol.replace(/USDT$|USDC$|USD$/, '');
                          const quote = coin?.quoteAsset ?? (item.symbol.endsWith('USDC') ? 'USDC' : item.symbol.endsWith('USD') ? 'USD' : 'USDT');
                          const menuId = `item-${item.id}`;
                          return (
                            <tr key={item.id}>
                              <td>
                                <Link
                                  href={`/coin/${item.symbol}${isSpot ? '?type=spot' : ''}`}
                                  className="flex items-center gap-2 whitespace-nowrap font-medium hover:text-gf-accent"
                                >
                                  <CoinLogo symbol={item.symbol} size={28} />
                                  <div className="leading-tight">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                      <span className="text-gf-text">{base}</span>
                                      <span className="text-xs text-gf-muted">/ {quote}</span>
                                      <span className={`rounded px-1 py-px text-[9px] ${isSpot ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                                        {isSpot
                                          ? (language !== 'en' ? '现货' : 'Spot')
                                          : (language !== 'en' ? '永续合约' : 'Perp')}
                                      </span>
                                    </div>
                                    <div className="mt-0.5 text-[10px] text-gf-muted">{item.exchange || 'Binance'}</div>
                                  </div>
                                </Link>
                              </td>
                              <td className="whitespace-nowrap text-right font-mono">{coin ? formatPrice(coin.price) : '—'}</td>
                              <td className={`whitespace-nowrap text-right font-mono ${pct >= 0 ? 'positive' : 'negative'}`}>
                                {coin ? formatPercent(pct) : '—'}
                              </td>
                              <td className="whitespace-nowrap text-right font-mono">{coin ? formatVolume(coin.quoteVolume24h) : '—'}</td>
                              <td className="relative text-right">
                                <button
                                  className="rounded px-1.5 text-gf-muted hover:bg-gf-surface2"
                                  onClick={() => setItemMenuFor(itemMenuFor === menuId ? null : menuId)}
                                >
                                  ⋮
                                </button>
                                {itemMenuFor === menuId && (
                                  <div className="absolute right-0 top-7 z-20 rounded-lg border border-gf-border bg-gf-surface p-1 shadow-xl">
                                    <Link
                                      href={`/coin/${item.symbol}${isSpot ? '?type=spot' : ''}`}
                                      className="block w-full whitespace-nowrap rounded px-3 py-1.5 text-left text-xs hover:bg-gf-surface2"
                                    >
                                      {t.markets.research} →
                                    </Link>
                                    <button
                                      className="block w-full whitespace-nowrap rounded px-3 py-1.5 text-left text-xs text-gf-negative hover:bg-gf-surface2"
                                      onClick={() => {
                                        setItemMenuFor(null);
                                        doRemove(item);
                                      }}
                                    >
                                      {t.watchlist.remove}
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile rows */}
                  <div className="divide-y divide-gf-border/60 sm:hidden">
                    {displayItems.map((item) => {
                      const coin = item.coin ? mergeLive(item.coin, live[item.coin.symbol]) : null;
                      const pct = coin?.priceChangePercent24h ?? 0;
                      const isSpot = (item.product === 'spot');
                      const base = coin?.baseAsset ?? item.symbol.replace(/USDT$|USDC$|USD$/, '');
                      const quote = coin?.quoteAsset ?? (item.symbol.endsWith('USDC') ? 'USDC' : item.symbol.endsWith('USD') ? 'USD' : 'USDT');
                      const menuId = `item-${item.id}`;
                      return (
                        <div key={item.id} className="flex items-center gap-2 px-3 py-2.5">
                          <Link href={`/coin/${item.symbol}${isSpot ? '?type=spot' : ''}`} className="flex min-w-0 flex-1 items-center gap-2">
                            <CoinLogo symbol={item.symbol} size={24} />
                            <div className="min-w-0 leading-tight">
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                <span className="text-sm font-medium">{base}</span>
                                <span className="text-[10px] text-gf-muted">/ {quote}</span>
                                <span className={`rounded px-1 py-px text-[9px] ${isSpot ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                                  {isSpot
                                    ? (language !== 'en' ? '现货' : 'Spot')
                                    : (language !== 'en' ? '永续' : 'Perp')}
                                </span>
                              </div>
                              <div className="text-[10px] text-gf-muted">{item.exchange || 'Binance'}</div>
                            </div>
                          </Link>
                          <div className="shrink-0 text-right">
                            <div className="font-mono text-sm">{coin ? formatPrice(coin.price) : '—'}</div>
                            <div className={`font-mono text-xs ${pct >= 0 ? 'positive' : 'negative'}`}>{coin ? formatPercent(pct) : '—'}</div>
                          </div>
                          <div className="relative shrink-0">
                            <button
                              className="rounded px-1.5 py-1 text-gf-muted hover:bg-gf-surface2"
                              onClick={() => setItemMenuFor(itemMenuFor === menuId ? null : menuId)}
                            >
                              ⋮
                            </button>
                            {itemMenuFor === menuId && (
                              <div className="absolute right-0 top-7 z-20 rounded-lg border border-gf-border bg-gf-surface p-1 shadow-xl">
                                <Link
                                  href={`/coin/${item.symbol}${isSpot ? '?type=spot' : ''}`}
                                  className="block w-full whitespace-nowrap rounded px-3 py-1.5 text-left text-xs hover:bg-gf-surface2"
                                >
                                  {t.markets.research} →
                                </Link>
                                <button
                                  className="block w-full whitespace-nowrap rounded px-3 py-1.5 text-left text-xs text-gf-negative hover:bg-gf-surface2"
                                  onClick={() => {
                                    setItemMenuFor(null);
                                    doRemove(item);
                                  }}
                                >
                                  {t.watchlist.remove}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </>
      )}

      {/* New group modal */}
      {showNewGroup && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNewGroup(false)} />
          <div className="relative z-10 w-full rounded-t-2xl border-t border-gf-border bg-gf-surface p-5 shadow-2xl sm:max-w-[420px] sm:rounded-2xl sm:border">
            <h3 className="mb-3 text-base font-semibold">{t.watchlist.newGroup}</h3>
            <input
              autoFocus
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder={t.watchlist.groupName}
              className="input !h-10"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGroupName.trim()) createGroup.mutate(newGroupName.trim());
              }}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-ghost !h-9" onClick={() => setShowNewGroup(false)}>
                {t.common.cancel}
              </button>
              <button
                className="btn-primary !h-9"
                disabled={!newGroupName.trim() || createGroup.isPending}
                onClick={() => createGroup.mutate(newGroupName.trim())}
              >
                {t.watchlist.newGroup}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add coin search modal */}
      {showAddCoin && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddCoin(false)} />
          <div className="relative z-10 flex h-[80vh] w-full flex-col rounded-t-2xl border-t border-gf-border bg-gf-surface shadow-2xl sm:h-auto sm:max-h-[70vh] sm:max-w-[520px] sm:rounded-2xl sm:border">
            <div className="border-b border-gf-border p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-base font-semibold">{t.watchlist.addCoin}</h3>
                <button className="rounded px-2 py-0.5 text-gf-muted hover:bg-gf-surface2" onClick={() => setShowAddCoin(false)}>
                  ✕
                </button>
              </div>
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder={t.markets.searchPlaceholder}
                className="input !h-10"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {searchQ.trim().length === 0 ? (
                <div className="p-8 text-center text-xs text-gf-muted">{t.watchlist.searchHint}</div>
              ) : searchPairs.length === 0 ? (
                <EmptyState compact title={t.markets.noResults} description={language !== 'en' ? '换个关键字试试' : 'Try another keyword.'} />
              ) : (
                <div className="divide-y divide-gf-border/60">
                  {searchPairs.map((p) => {
                    const added = inGroup(p.symbol, p.exchange, p.product);
                    return (
                      <div key={marketKey(p.symbol, p.exchange, p.product)} className="flex items-center gap-2 px-4 py-2.5">
                        <CoinLogo symbol={p.symbol} size={24} />
                        <div className="min-w-0 flex-1 leading-tight">
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-sm font-medium">{p.baseAsset}</span>
                            <span className="text-[10px] text-gf-muted">/{p.quoteAsset}</span>
                            <span className={`rounded px-1 py-px text-[9px] ${p.isPerp ? 'bg-gf-accent/10 text-gf-accent' : 'bg-gf-surface2 text-gf-muted'}`}>
                              {p.isPerp
                                ? (language !== 'en' ? '永续' : 'Perp')
                                : (language !== 'en' ? '现货' : 'Spot')}
                            </span>
                          </div>
                          <div className="text-[10px] text-gf-muted">{p.exchange} · {p.symbol}</div>
                        </div>
                        {added ? (
                          <span className="shrink-0 text-xs text-gf-positive">{t.watchlist.alreadyAdded}</span>
                        ) : (
                          <button
                            className="btn-primary !h-7 !px-2.5 text-[11px]"
                            disabled={addItem.isPending}
                            onClick={() => addItem.mutate({ groupId: activeGroup?.id ?? '', symbol: p.symbol, exchange: p.exchange, product: p.product })}
                          >
                            + {t.watchlist.add}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}
